import React from "react";
import { motion } from "framer-motion";
import Button from "../../../components/ui/Button";
import { cardData } from "./data";

const ToHelp: React.FC = () => {
  const handleRequestDemo = () => {
    window.open(
      "https://cal.com/seamlessvisa/consultation",
      "_blank",
    );
  };

  // Map of hex colors to tailwind color themes for the bento cards
  const colorMap: Record<string, { bg: string; text: string; light: string }> =
    {
      "#0076E9": {
        bg: "bg-blue-600",
        text: "text-blue-600",
        light: "bg-blue-50",
      },
      "#FF9900": {
        bg: "bg-amber-600",
        text: "text-amber-600",
        light: "bg-amber-50",
      },
      "#0DA700": {
        bg: "bg-green-600",
        text: "text-green-600",
        light: "bg-green-50",
      },
      "#FF00C7": {
        bg: "bg-purple-600",
        text: "text-purple-600",
        light: "bg-purple-50",
      },
      "#9300C7": {
        bg: "bg-indigo-600",
        text: "text-indigo-600",
        light: "bg-indigo-50",
      },
      "#FF4E4E": { bg: "bg-red-600", text: "text-red-600", light: "bg-red-50" },
    };

  return (
    <section className="py-24 bg-white-f9 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-end justify-between gap-8 mb-20">
          <div className="max-w-3xl">
            <motion.h2
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-4xl md:text-5xl font-bold font-spartan text-black-1a leading-tight"
            >
              We are here to{" "}
              <span className="text-blue-00 italic mx-1">⚙️ help</span>{" "}
              your business win.
            </motion.h2>
            <p className="mt-6 text-xl text-dark-6b font-inter">
              Empower your practice with AI-driven tools that automate the
              tedious and amplify your expertise.
            </p>
          </div>
          <Button
            label="Request a Demo"
            onClick={handleRequestDemo}
            variant="primary"
            size="large"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cardData.map((card, index) => {
            const styles =
              colorMap[card.color as keyof typeof colorMap] ||
              colorMap["#0076E9"];

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group relative bg-white rounded-3xl p-10 border border-gray-100/50 hover:border-blue-00 transition-all duration-300 flex flex-col items-start gap-6 overflow-hidden shadow-sm hover:shadow-none"
              >
                <div
                  className={`h-14 w-14 ${styles.light} rounded-2xl flex items-center justify-center ${styles.text} text-2xl group-hover:scale-110 transition-transform duration-300`}
                >
                  {card.icon}
                </div>
                <div>
                  <h3 className="text-xl font-bold font-spartan text-black-1a mb-4 group-hover:text-blue-00 transition-colors">
                    {card.title}
                  </h3>
                  <p className="text-dark-6b leading-relaxed font-inter">
                    {card.description}
                  </p>
                </div>

                {/* Decorative subtle gradient on hover */}
                <div className="absolute inset-0 bg-linear-to-br from-transparent to-white/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-3xl -z-10" />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ToHelp;
